import React, { useEffect, useState } from "react";
import { FaChevronRight } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { postApiCallerFormData } from "../../libs/apiCaller";

const EditProviderInfo = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const provider = location.state?.provider; // Get provider object from navigation state
  const [profileImage, setProfileImage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (provider) {
      setProfileImage(
        provider?.profile_picture ||
          "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTqafzhnwwYzuOTjTlaYMeQ7hxQLy_Wq8dnQg&s"
      );
      setFormData({
        provider_id: provider.id,
        email: provider.email,
        first_name: provider.first_name,
        last_name: provider.last_name,
        billing_apt_no: provider.billing_details?.billing_apt_no || "",
        billing_street: provider.billing_details?.billing_street || "",
        billing_city: provider.billing_details?.billing_city || "",
        billing_state: provider.billing_details?.billing_state || "",
        billing_postal_code:
          provider.billing_details?.billing_postal_code || "",
        service_id: provider.service_details?.service_id || "",
        country_code: provider.country_code,
        number: provider.number,
        appointment_fee: provider.appointment_fee,
        appointment_till_date: provider.appointment_till_date,
        license_country: provider.license_details?.license_country || "",
        license_no: provider.license_details?.license_no || "",
        license_state: provider.license_details?.license_state || "",
        profile_picture: provider?.profile_picture,
        experience_in: provider.experience_in
          ? provider.experience_in
              .filter((exp) => exp.is_selected)
              .map((exp) => exp.id)
          : [],
      });
    }
  }, [provider]);

  // Handle Profile Picture Change
  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setProfileImage(imageUrl);
      setFormData({ ...formData, profile_picture: file });
    }
  };

  // Handle input change
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Handle radio button change for Country Nursing License
  const handleCountryChange = (e) => {
    setFormData({ ...formData, license_country: e.target.value });
  };

  const handleAppointmentTillDateChange = (e) => {
    setFormData({ ...formData, appointment_till_date: e.target.value });
  };

  const handleServiceChange = (e) => {
    setFormData({ ...formData, service_id: e.target.value });
  };

  // Handle checkbox change for Experience
  const handleCheckboxChange = (id) => {
    setFormData((prevState) => {
      const updatedExperience = prevState.experience_in.includes(id)
        ? prevState.experience_in.filter((expId) => expId !== id)
        : [...prevState.experience_in, id];
      return { ...prevState, experience_in: updatedExperience };
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate image size before submitting
    if (
      formData?.profile_picture &&
      formData?.profile_picture?.size > 5 * 1024 * 1024
    ) {
      toast.error("Image size must be below 5MB.");
      return;
    }

    const updatedData = new FormData();
    Object.keys(formData).forEach((key) => {
      if (key === "experience_in") {
        updatedData.append(key, JSON.stringify(formData[key])); // Convert array to JSON string
      } else {
        updatedData.append(key, formData[key]);
      }
    });

    try {
      setIsLoading(true);
      await postApiCallerFormData("provider_update", updatedData);
      toast.success("Provider updated successfully!");
      navigate("/provider");
      setFormData({});
      setProfileImage("");
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      toast.error("Failed to update provider. Please try again.");
    }
  };

  return (
    <>
      <div className="px-4 md:px-10">
        {/* Breadcrumb */}
        <div className="flex flex-col sm:flex-row mt-6 gap-2 items-start sm:items-center justify-between">
          <div className="flex text-sm gap-2 items-center">
            <a href="/provider" className="text-[#284184] text-sm sm:text-xl">
              All Providers
            </a>
            <FaChevronRight className="text-[#284184] text-md" />

            <button
              href="/provider"
              className="text-[#284184] text-xl font-[Lato-Regular] hover:underline"
              onClick={() =>
                navigate("/providerProfile", {
                  state: { provider },
                })
              }
            >
              {provider
                ? `${provider?.first_name} ${provider?.last_name}`
                : "Provider Name"}
            </button>
            <span className="text-[#284184] text-sm sm:text-xl"></span>
            <FaChevronRight className="text-[#284184] text-md" />
            <span className="text-[#284184] text-sm sm:text-xl font-bold">
              Edit Profile
            </span>
          </div>
        </div>
        <div className="border border-1 px-4 pt-3 pb-2 rounded-t-2xl text-xl mt-4 bg-white">
          <div className="flex justify-between">
            <p className="text-[#284184] font-bold">Profile Info</p>
            <button
              className="bg-[#284184] text-white px-4 pt-1 pb-2 rounded-xl text-base flex items-center space-x-1"
              onClick={handleSubmit}
              disabled={isLoading}
            >
              {isLoading ? "Loading..." : "Save Changes"}
            </button>
          </div>
        </div>

        <div className="p-2 border border-t-0 rounded-b-2xl px-4 py-4 bg-white">
          <div className="flex items-center gap-4 mb-8">
            <div className="col-span-1">
              <div className="flex items-center space-x-4">
                <div className="rounded-xl bg-gray-200 flex items-center justify-center">
                  <img
                    src={
                      profileImage ||
                      "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTqafzhnwwYzuOTjTlaYMeQ7hxQLy_Wq8dnQg&s"
                    }
                    alt={"profile"}
                    className="w-24 h-24 rounded-xl"
                  />
                </div>
              </div>
            </div>
            <label className="relative cursor-pointer">
              <p className="text-base text-[#E0A256] underline">Change Photo</p>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageChange}
              />
            </label>
          </div>

          <form className="space-y-6">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-base font-semibold text-gray-700 mb-1">
                  First name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border rounded-md"
                  value={formData?.first_name}
                  name="first_name"
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="block text-base font-semibold text-gray-700 mb-1">
                  Last name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border rounded-md"
                  name="last_name"
                  value={formData?.last_name}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="block text-base font-semibold text-gray-700 mb-1">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  className="w-full px-3 py-2 border rounded-md"
                  name="email"
                  value={formData?.email}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="block text-base font-semibold text-gray-700 mb-1">
                  Mobile Number <span className="text-red-500">*</span>
                </label>
                <div className="flex">
                  <select className="px-2 py-2 border rounded-l-md border-r-0">
                    <option>🇨🇦 +1</option>
                  </select>
                  <input
                    type="tel"
                    className="w-full px-3 py-2 border rounded-r-md"
                    name="number"
                    value={formData?.number}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-base font-semibold text-gray-700 mb-1">
                About
              </label>
              <textarea
                className="w-full px-3 py-2 border rounded-md"
                rows={4}
                placeholder="I am nurse with over 10 years experience in foot care for seniors. I specialize in treating corn and callus"
              />
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-base font-semibold text-gray-700 mb-1">
                  Apt No
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border rounded-md"
                  name="billing_apt_no"
                  value={formData?.billing_apt_no || ""}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="block text-base font-semibold text-gray-700 mb-1">
                  Street <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border rounded-md"
                  name="billing_street"
                  value={formData?.billing_street}
                  onChange={handleChange}
                />
              </div>

              <div>
                <label className="block text-base font-semibold text-gray-700 mb-1">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border rounded-md"
                  name="billing_city"
                  value={formData?.billing_city}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="block text-base font-semibold text-gray-700 mb-1">
                  Postal Code
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border rounded-md"
                  name="billing_postal_code"
                  value={formData?.billing_postal_code}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-base font-semibold text-gray-700 mb-1">
                  State/Province for Nursing License{" "}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border rounded-md"
                  name="license_state"
                  value={formData?.license_state}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="block text-base font-semibold text-gray-700 mb-1">
                  License/Registration No{" "}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border rounded-md"
                  name="license_no"
                  value={formData?.license_no}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="block text-base font-semibold text-gray-700 mb-1">
                  Appointment Fee <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border rounded-md"
                  name="appointment_fee"
                  value={formData?.appointment_fee}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div>
              <label className="block text-base font-semibold text-gray-700 mb-3">
                I am experienced in <span className="text-red-500">*</span>
              </label>
              <div className="space-y-2">
                {provider?.experience_in?.map((exp) => (
                  <label key={exp.id} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      className="rounded"
                      checked={formData?.experience_in?.includes(exp.id)}
                      onChange={() => handleCheckboxChange(exp.id)}
                    />
                    <span>{exp.question}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-base font-semibold text-gray-700 mb-3">
                Which service you offering?{" "}
                <span className="text-red-500">*</span>
              </label>

              <div className="flex gap-5">
                {[
                  ["Foot Care Nurse", "1"],
                  ["Physiotherapist (Coming Soon)", "2"],
                  ["Chiropodist (Coming Soon)", "3"],
                ].map((service) => (
                  <label key={service} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="service"
                      value={service?.[1]}
                      checked={formData?.service_id === service[1]}
                      onChange={handleServiceChange}
                      disabled={service?.[1] !== "1"}
                    />
                    <span>{service?.[0]}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-base font-semibold text-gray-700 mb-3">
                Foot Care Appointments till date{" "}
                <span className="text-red-500">*</span>
              </label>

              <div className="flex gap-5">
                {["50", "200", "500"].map((appointment_till_date) => (
                  <label
                    key={appointment_till_date}
                    className="flex items-center gap-2"
                  >
                    <input
                      type="radio"
                      name="appointment_till_date"
                      value={appointment_till_date}
                      checked={
                        formData?.appointment_till_date ===
                        appointment_till_date
                      }
                      onChange={handleAppointmentTillDateChange}
                    />
                    <span>{appointment_till_date}+</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-base font-semibold text-gray-700 mb-3">
                Country Nursing License <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-5">
                {["US", "Canada"].map((country) => (
                  <label key={country} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="license_country"
                      value={country}
                      checked={formData?.license_country === country}
                      onChange={handleCountryChange}
                    />
                    <span>{country}</span>
                  </label>
                ))}
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default EditProviderInfo;
